import type { PipelineDefinition } from './PipelineDefinition';
import { PublishToSubscribersHook } from 'edge-stream-js';

// Common hooks
import { LogHook, FilterHook } from '../hooks/common';
// Incoming hooks
import { ValidationHook as IncomingValidationHook, CustomIncomingHook } from '../hooks/incoming';
// Outgoing hooks
import { ValidationHook as OutgoingValidationHook, CustomOutgoingHook } from '../hooks/outgoing';
// Subscribers
import { CustomIncomingSubscriber } from '../subscribers/incoming';
import { CustomOutgoingSubscriber } from '../subscribers/outgoing';

export const DefaultIncomingPipeline: PipelineDefinition = {
  metadata: {
    id: 'edgestream.default-incoming-v1',
    name: 'Default Incoming Pipeline',
    direction: 'incoming',
    description:
      'Standard incoming pipeline: logging, validation, filtering, custom enrichment, and publish to subscribers.',
    version: '1.0.0',
    author: 'EdgeStream',
    tags: ['standard', 'incoming', 'validation', 'enrichment'],
    impactLevel: 'high',
  },
  createHooks: () => [
    new LogHook(),
    new IncomingValidationHook(),
    new FilterHook(),
    new CustomIncomingHook(),
    new PublishToSubscribersHook(),
  ],
  createSubscribers: () => [new CustomIncomingSubscriber()],
};

export const SecurityIncomingPipeline: PipelineDefinition = {
  metadata: {
    id: 'edgestream.security-incoming-v1',
    name: 'Security Incoming Pipeline',
    direction: 'incoming',
    description:
      'Security-first incoming pipeline. Applies strict validation before any enrichment. Recommended for untrusted sources.',
    version: '1.0.0',
    author: 'EdgeStream',
    tags: ['security', 'incoming', 'strict-validation'],
    impactLevel: 'critical',
  },
  createHooks: () => [
    new LogHook(), // Priority 10 - log first
    new IncomingValidationHook(), // Priority 20 - validate early (throws on failure)
    new FilterHook(), // Priority 50 - filter after validation
    new PublishToSubscribersHook(), // Priority 200 - skip enrichment for security contexts
  ],
  // No createSubscribers — adapter will use its defaults
};

export const DefaultOutgoingPipeline: PipelineDefinition = {
  metadata: {
    id: 'edgestream.default-outgoing-v1',
    name: 'Default Outgoing Pipeline',
    direction: 'outgoing',
    description:
      'Standard outgoing pipeline: logging, validation, filtering, transformation (signing, classification, compression), and publish.',
    version: '1.0.0',
    author: 'EdgeStream',
    tags: ['standard', 'outgoing', 'transformation', 'signing'],
    impactLevel: 'high',
  },
  createHooks: () => [
    new LogHook(),
    new OutgoingValidationHook(),
    new FilterHook(),
    new CustomOutgoingHook(),
    new PublishToSubscribersHook(),
  ],
  createSubscribers: () => [new CustomOutgoingSubscriber()],
};

export const MinimalOutgoingPipeline: PipelineDefinition = {
  metadata: {
    id: 'edgestream.minimal-outgoing-v1',
    name: 'Minimal Outgoing Pipeline',
    direction: 'outgoing',
    description:
      'Lightweight outgoing pipeline with only validation and publish. Suitable for high-throughput, low-latency scenarios where transformation overhead is undesired.',
    version: '1.0.0',
    author: 'EdgeStream',
    tags: ['minimal', 'outgoing', 'performance', 'lightweight'],
    impactLevel: 'medium',
  },
  createHooks: () => [
    new LogHook(),
    new OutgoingValidationHook(),
    new PublishToSubscribersHook(),
  ],
  // No createSubscribers — adapter will use its defaults
};

/** All built-in pipeline definitions, registered automatically by PipelineRegistry. */
export const STANDARD_PIPELINES: PipelineDefinition[] = [
  DefaultIncomingPipeline,
  SecurityIncomingPipeline,
  DefaultOutgoingPipeline,
  MinimalOutgoingPipeline,
];
